'use client';

import { useEffect, useState } from 'react';

import Image from 'next/image';

import { motion } from 'framer-motion';

interface ConfettiPieceType {
  id: number
  color: string
  type: string
  size: number
  delay: number
  xPosition: number
  duration: number
  rotation: number
}

interface RadialPieceType {
  id: number
  color: string
  type: string
  size: number
  delay: number
  angle: number
  distance: number
  duration: number
  rotation: number
}

const CONFETTI_TYPES = ['square', 'rectangle', 'circle'];
const CONFETTI_COLORS = [
  '#FFD700',
  '#F5DEB3',
  '#FFF8DC',
  '#D4AF37',
  '#E5C07B',
  '#C0B283',
  '#FAF3E0',
];

const ConfettiPiece = ({
  id, color, type, size, delay, xPosition, duration, rotation,
}: ConfettiPieceType) => {
  const getShape = () => {
    switch (type) {
      case 'square':
        return {
          width: size,
          height: size,
          borderRadius: '0%',
          id,
        };
      case 'rectangle':
        return {
          width: size,
          height: size * 0.5,
          borderRadius: '0%',
          id,
        };
      case 'circle':
        return {
          width: size,
          height: size,
          borderRadius: '50%',
          id,
        };
      default:
        return {
          width: size,
          height: size,
          borderRadius: '0%',
        };
    }
  };

  const shape = getShape();

  return (
    <motion.div
      className="absolute"
      style={{
        backgroundColor: color,
        width: shape.width,
        height: shape.height,
        borderRadius: shape.borderRadius,
        zIndex: 50,
        top: '-20px',
        left: `${xPosition}%`,
        opacity: 0,
      }}
      initial={{
        y: '-5vh',
        rotate: 0,
        opacity: 0,
      }}
      animate={{
        y: ['0vh', '120vh'],
        rotate: [0, rotation],
        opacity: [0, 1, 1, 0.8, 0],
      }}
      transition={{
        duration,
        delay,
        ease: 'easeIn',
        times: [0, 0.1, 0.8, 0.95, 1],
      }}
    />
  );
};

const RadialPiece = ({
  id, color, type, size, delay, angle, distance, duration, rotation,
}: RadialPieceType) => {
  const getShape = () => {
    switch (type) {
      case 'square':
        return {
          width: size,
          height: size,
          borderRadius: '0%',
          id,
        };
      case 'rectangle':
        return {
          width: size,
          height: size * 0.5,
          borderRadius: '0%',
          id,
        };
      case 'circle':
        return { width: size, height: size, borderRadius: '50%' };
      default:
        return {
          width: size,
          height: size,
          borderRadius: '0%',
          id,
        };
    }
  };

  const shape = getShape();

  const finalX = Math.cos(angle) * distance;
  const finalY = Math.sin(angle) * distance;

  return (
    <motion.div
      className="absolute"
      style={{
        backgroundColor: color,
        width: shape.width,
        height: shape.height,
        borderRadius: shape.borderRadius,
        zIndex: 5,
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        opacity: 0,
      }}
      initial={{
        x: 0,
        y: 0,
        rotate: 0,
        opacity: 0,
      }}
      animate={{
        x: [0, finalX],
        y: [0, finalY],
        rotate: [0, rotation],
        opacity: [0, 1, 0.8, 0],
      }}
      transition={{
        duration,
        delay,
        ease: 'easeOut',
        times: [0, 0.2, 0.8, 1],
      }}
    />
  );
};

const WinLayout = ({
  show,
  onClose,
  view,
  setView,
}: {
  show: boolean
  onClose: () => void
  view: string
  setView: (view: number) => void
}) => {
  const [visible, setVisible] = useState(show);
  const prize = view === 'casino' ? '/img/v2/register/spins.webp' : '/img/v2/register/L100.webp';

  const [isPlaying, setIsPlaying] = useState(false);
  const [confettiPieces, setConfettiPieces] = useState<ConfettiPieceType[]>([]);
  const [radialPieces, setRadialPieces] = useState<RadialPieceType[]>([]);
  const [confettiCount, setConfettiCount] = useState(0);
  const maxPieces = 300;

  const generateConfettiPiece = () => {
    const isLateral = Math.random() < 0.8;
    let xPosition;
    if (isLateral) {
      xPosition = Math.random() < 0.5 ? Math.random() * 20 : 80 + Math.random() * 20;
    } else {
      xPosition = 20 + Math.random() * 60;
    }

    return {
      id: Date.now() + Math.random(),
      color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
      type: CONFETTI_TYPES[Math.floor(Math.random() * CONFETTI_TYPES.length)],
      size: Math.random() * 6 + 5,
      delay: Math.random() * 3,
      xPosition,
      duration: Math.random() * 2 + 2,
      rotation: (Math.random() - 0.5) * 720,
    };
  };

  const generateRadialPieces = (count: number) => {
    const pieces: RadialPieceType[] = [];

    for (let i = 0; i < count; i++) {
      const angle = Math.random() * Math.PI * 4;
      const distance = Math.random() * 400 + 50;

      pieces.push({
        id: Date.now() + Math.random(),
        color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
        type: CONFETTI_TYPES[Math.floor(Math.random() * CONFETTI_TYPES.length)],
        size: Math.random() * 8 + 4,
        delay: Math.random() * 0.2,
        angle,
        distance,
        duration: Math.random() * 1 + 0.8,
        rotation: (Math.random() - 0.5) * 720,
      });
    }

    return pieces;
  };

  useEffect(() => {
    if (!isPlaying) return;

    const interval = setInterval(() => {
      if (confettiCount >= maxPieces) {
        clearInterval(interval);
        setIsPlaying(false);
        return;
      }

      const newPieces: ConfettiPieceType[] = [];
      const piecesToAdd = Math.floor(Math.random() * 200) + 1;

      for (let i = 0; i < piecesToAdd; i++) {
        newPieces.push(generateConfettiPiece());
      }

      setConfettiPieces((prev) => {
        return [...prev, ...newPieces];
      });
      setConfettiCount((prev) => {
        return prev + piecesToAdd;
      });

      if (confettiPieces.length > 100) {
        setConfettiPieces((prev) => {
          return prev.slice(prev.length - 100);
        });
      }
    }, 100);

    // eslint-disable-next-line consistent-return
    return () => {
      clearInterval(interval);
    };
  }, [isPlaying, confettiCount, confettiPieces.length]);

  useEffect(() => {
    if (show) {
      setVisible(true);
      setIsPlaying(true);
      setConfettiCount(0);
      setConfettiPieces([]);

      setRadialPieces(generateRadialPieces(100));

      // const secondExplosionTimeout = setTimeout(() => {
      //   setRadialPieces(generateRadialPieces(40));
      // }, 2000);

      const timeout = setTimeout(() => {
        setVisible(false);
        setView(1);
        onClose();
        setIsPlaying(false);
        setConfettiPieces([]);
        setRadialPieces([]);
      }, 5000);

      return () => {
        clearTimeout(timeout);
        // clearTimeout(secondExplosionTimeout);
      };
    }
    return undefined;
  }, [show, onClose, setView]);

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center">
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        {radialPieces.map((piece) => {
          return (
            <RadialPiece
              key={piece.id}
              id={piece.id}
              color={piece.color}
              type={piece.type}
              size={piece.size}
              delay={piece.delay}
              angle={piece.angle}
              distance={piece.distance}
              duration={piece.duration}
              rotation={piece.rotation}
            />
          );
        })}
      </div>
      <Image
        src={prize}
        alt="Felicitaciones"
        width={180}
        height={180}
        className="z-10 relative animate-pulse"
        loading="eager"
        priority
      />

      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        {confettiPieces.map((piece) => {
          return (
            <ConfettiPiece
              key={piece.id}
              id={piece.id}
              color={piece.color}
              type={piece.type}
              size={piece.size}
              delay={piece.delay}
              xPosition={piece.xPosition}
              duration={piece.duration}
              rotation={piece.rotation}
            />
          );
        })}
      </div>
    </div>
  );
};

export default WinLayout;
